"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, loadTokens } from "@/lib/api";
import {
  readRegionCookie,
  writeRegionCookie,
  type RegionState,
  type SupportedLocale,
} from "@/lib/regionContext";

interface LocalePickerProps {
  // Compact = header pill (icon-only collapse on small screens)
  // Full = settings page (label + dropdown)
  // Footer = Polymarket-style discrete footer button — flag + label,
  //          44px touch target, dropdown opens UPWARD so it stays
  //          on-screen at the bottom of the viewport (2026-05-24).
  variant: "compact" | "full" | "footer";
}

// 2026-05-03 — expanded to all Phase-1 regions per the locked
// product vision (BR + MX + CA + IN + global default). The label
// for each option is the locale's autonym (Português in pt-BR
// always, etc.) so the picker reads as "the language you're
// switching TO" regardless of the active locale.
//
// India: en-IN catalog hasn't shipped (deferred per EPIC-030); the
// 🇮🇳 option maps to 'en' locale + 'india' region so Indian users
// get the surface-language they'd see anyway, but their region
// cookie reflects the actual location for downstream geo-conditional
// behaviour (KYC routing, regulatory copy in STORY-2211, etc.).
//
// Currency stays USDC-as-truth per EPIC-029 STORY-1716. Per-region
// display-currency projection (BRL/MXN/CAD/INR) lives in the user's
// `display_currency` setting + lib/format helpers, not in the
// region cookie.
// Unique per-option key — needed because (locale, region) alone
// isn't a discriminator (e.g. en-CA + en-CA Quebec; en + en India
// + en US-default all share the locale field).
type OptionKey = "br" | "mx" | "ca-en" | "ca-fr" | "in" | "us";

const OPTIONS: Array<{
  key: OptionKey;
  locale: SupportedLocale;
  flag: string;
  label: string;
  region: "brazil" | "mexico" | "canada" | "india" | "global";
  currency: "BRL" | "USD" | "USDC";
}> = [
  {
    key: "br",
    locale: "pt",
    flag: "🇧🇷",
    label: "Português",
    region: "brazil",
    currency: "BRL",
  },
  {
    key: "mx",
    locale: "es-MX",
    flag: "🇲🇽",
    label: "Español (México)",
    region: "mexico",
    currency: "USDC",
  },
  {
    key: "ca-en",
    locale: "en-CA",
    flag: "🇨🇦",
    label: "English (Canada)",
    region: "canada",
    currency: "USDC",
  },
  {
    key: "ca-fr",
    locale: "fr-CA",
    flag: "🇨🇦",
    label: "Français (Canada)",
    region: "canada",
    currency: "USDC",
  },
  {
    key: "in",
    locale: "en",
    flag: "🇮🇳",
    label: "English (India)",
    region: "india",
    currency: "USDC",
  },
  {
    key: "us",
    locale: "en",
    flag: "🇺🇸",
    label: "English",
    region: "global",
    currency: "USD",
  },
];

// Pick the active option: prefer (locale, region) match exactly, else
// match locale only, else fall through to the "us" default.
function findActiveOption(state: RegionState) {
  return (
    OPTIONS.find(
      (o) => o.locale === state.locale && o.region === state.region,
    ) ??
    OPTIONS.find((o) => o.locale === state.locale) ??
    OPTIONS.find((o) => o.key === "us")!
  );
}

/**
 * EPIC-028 STORY-1604 — locale picker for header + settings.
 *
 * Selecting a locale:
 *   1. Writes granbay.region cookie (detected: false → never auto-
 *      override). Server-rendered subsequent pages immediately see
 *      the choice
 *   2. If authed, persists via PATCH /v1/me/locale so a fresh-login
 *      on a new device hydrates from the same pref
 *   3. Triggers a window.location.reload() so server-rendered chrome
 *      (BetaBanner, SiteFooter, RegionDetector) re-runs with the new
 *      locale immediately
 */
export function LocalePicker({ variant }: LocalePickerProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<RegionState | null>(null);
  const [authed, setAuthed] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState(readRegionCookie());
    setAuthed(loadTokens() !== null);
  }, []);

  if (!state) return null;

  async function pick(key: OptionKey) {
    if (pending || !state) return;
    const opt = OPTIONS.find((o) => o.key === key);
    if (!opt) return;
    // No-op if the user clicks the already-active option.
    const active = findActiveOption(state);
    if (active.key === key) return;
    setPending(true);
    const next: RegionState = {
      region: opt.region,
      locale: opt.locale,
      currency: opt.currency,
      detected: false,
      countryCode: state.countryCode,
    };
    writeRegionCookie(next);
    if (authed) {
      try {
        await api.updateLocale(opt.locale);
      } catch {
        // Cookie's already written — server-side rendering picks it
        // up. The /v1/me/locale persistence is best-effort.
      }
    }
    window.location.reload();
  }

  if (variant === "compact") {
    return <CompactLocalePicker state={state} pending={pending} pick={pick} />;
  }
  if (variant === "footer") {
    return <FooterLocalePicker state={state} pending={pending} pick={pick} />;
  }

  return (
    <FullLocalePicker
      state={state}
      pending={pending}
      pick={pick}
      label={t("localePicker.label")}
    />
  );
}

/**
 * Footer variant — Polymarket-style discrete picker at the page
 * bottom (2026-05-24). Flag + label trigger reads like the rest of
 * the footer nav, 44px touch target, dropdown opens UPWARD so it
 * stays on-screen at the bottom of the viewport without forcing a
 * scroll. Each option row is 44px tall for mobile touch comfort.
 */
function FooterLocalePicker({
  state,
  pending,
  pick,
}: {
  state: RegionState;
  pending: boolean;
  pick: (key: OptionKey) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const active = findActiveOption(state);
  const others = OPTIONS.filter((o) => o.key !== active.key);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        wrapRef.current &&
        e.target instanceof Node &&
        !wrapRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Short locale code for the trigger ("EN" / "PT" / "ES" / "FR").
  // Polymarket / Linear / Stripe footer pattern: minimal text +
  // chevron, no flag in the closed state. Flags reappear in the
  // dropdown rows where they help users recognize the option.
  const shortCode = active.locale.slice(0, 2).toUpperCase();

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={active.label}
        className="inline-flex min-h-[44px] items-center gap-1 px-1.5 text-[11px] font-medium transition-colors hover:text-[var(--color-text)]"
        style={{ color: "var(--color-text2)" }}
      >
        <span className="leading-none tracking-[0.04em]">{shortCode}</span>
        <ChevronDown
          size={11}
          strokeWidth={1.75}
          aria-hidden="true"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && others.length > 0 && (
        <ul
          role="listbox"
          aria-label={t("localePicker.switchLanguageAria")}
          className="absolute bottom-full right-0 z-50 mb-1 min-w-[200px] overflow-hidden rounded-lg border"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
            boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
          }}
        >
          {others.map((opt) => (
            <li key={opt.key} role="option" aria-selected={false}>
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setOpen(false);
                  void pick(opt.key);
                }}
                className="flex w-full min-h-[40px] items-center gap-2.5 border-t px-3 py-2 text-left text-[12px] font-medium transition-colors first:border-t-0 hover:bg-[var(--color-bg)] disabled:opacity-50"
                style={{
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              >
                <span aria-hidden className="text-[14px] leading-none">
                  {opt.flag}
                </span>
                <span className="leading-tight">{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Compact variant — header chrome.
 *
 * Founder UX feedback 2026-05-01: rendering both flags side-by-side
 * burns header real estate and lets the user mistake the inactive
 * flag for a "switch to" affordance whose semantics aren't obvious.
 * Replace with a single-flag trigger that opens a dropdown listing
 * the alternatives. Hover (desktop) + click (touch) both reveal the
 * menu; click an option to switch + reload.
 */
function CompactLocalePicker({
  state,
  pending,
  pick,
}: {
  state: RegionState;
  pending: boolean;
  pick: (key: OptionKey) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const active = findActiveOption(state);
  const others = OPTIONS.filter((o) => o.key !== active.key);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        wrapRef.current &&
        e.target instanceof Node &&
        !wrapRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Click-only open. Founder bug 2026-05-01: the previous
  // mouseenter/mouseleave + 4px-gap implementation closed the dropdown
  // before a click could land — the mouse momentarily left the
  // wrapper's hit-area as it crossed the absolute dropdown's offset.
  // Click-only is reliable across all input modalities, and the
  // browser's native `title=` tooltip on hover (which collided with
  // the dropdown rendering) is dropped in favour of `aria-label`.
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={active.label}
        className="rounded-md px-1.5 py-1 text-[14px]"
        style={{ background: "var(--color-brand-tint)" }}
      >
        <span aria-hidden>{active.flag}</span>
      </button>
      {open && others.length > 0 && (
        <ul
          role="listbox"
          aria-label={t("localePicker.switchLanguageAria")}
          className="absolute right-0 top-full z-50 mt-1 min-w-[180px] overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg"
          style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.15)" }}
        >
          {others.map((opt) => (
            <li key={opt.key} role="option" aria-selected={false}>
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setOpen(false);
                  void pick(opt.key);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-semibold transition-colors hover:bg-[var(--color-bg)] disabled:opacity-50"
              >
                <span aria-hidden className="text-[16px]">
                  {opt.flag}
                </span>
                <span>{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Full variant — used on /settings/profile or /settings/account.
 * Side-by-side option list with the active row borderless-brand-
 * tinted, others outlined. Identical to the pre-2026-05-01 compact
 * shape; kept here because the settings page benefits from the
 * extra context that a header chrome can't afford.
 */
function FullLocalePicker({
  state,
  pending,
  pick,
  label,
}: {
  state: RegionState;
  pending: boolean;
  pick: (key: OptionKey) => Promise<void>;
  label: string;
}) {
  const activeKey = findActiveOption(state).key;
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {OPTIONS.map((opt) => {
          const active = opt.key === activeKey;
          return (
            <button
              key={opt.key}
              type="button"
              disabled={pending || active}
              onClick={() => pick(opt.key)}
              aria-pressed={active}
              className="flex items-center gap-2 rounded-md border px-3 py-2 text-[13px] font-bold disabled:cursor-default"
              style={{
                borderColor: active
                  ? "var(--color-brand)"
                  : "var(--color-border)",
                background: active
                  ? "var(--color-brand-tint)"
                  : "var(--color-bg)",
                color: active ? "var(--color-brand)" : "var(--color-text2)",
              }}
            >
              <span aria-hidden>{opt.flag}</span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
